// Apple IAP provider — wraps cordova-plugin-purchase (CdvPurchase v13+).
// Canonical pattern per shared-docs/in-app-purchases.md.
//
// Critical rules enforced here (each prevents an App Store rejection or hang):
//   1. Three-event pipeline: approved → verify() → verified → silentFetch.
//      `approved` updates local state immediately; `verified` writes to backend.
//   2. User-initiated guard with a 5-min window — receipts that fire on app
//      launch (re-delivery) for transactions NOT initiated by the current user
//      MUST be ignored. Otherwise a fresh account on a device with stale receipts
//      inherits Premium from the previous owner.
//   3. ALL of 6777006/6777003/6777010 (+ any /cancel/i message) from
//      store.order() are USER CANCELS — not errors. Handling only one code
//      missed a reviewer's sheet-dismiss and got BabyTrack rejected under 2.1(b).
//   4. Verify backend writes via silentFetch (NOT apiPost) so a stale receipt
//      doesn't toast on every app launch forever.
import { APPLE_PRODUCT_IDS, APPLE_TO_TIER, PRODUCTS } from '../constants/subscription';
import { silentFetch } from '../data/apiClient';

const USER_INITIATED_WINDOW_MS = 5 * 60 * 1000;

const CANCEL_CODES = new Set([
  6777006, // PAYMENT_CANCELLED — Apple's real SKErrorPaymentCancelled mapping
  6777003, // PURCHASE — generic code seen on legacy iPadOS sandbox cancels
  6777010, // UNKNOWN — Ask-to-Buy deferral / unknown failure on cancel
]);

let initialised = false;
let userInitiatedAt = 0;
let listeners = new Set();

// Transactions the user just approved (purchase or restore), keyed
// transactionId → productId. The object CdvPurchase hands to `verified` is a
// VerifiedReceipt, which has NO `.transaction` and NO `.productId` — the old
// handler read them, got undefined, and the apple-verify POST never fired, so
// no Apple purchase was ever recorded server-side (fleet fix 2026-09-05).
// `approved` is the only place the id is known: it queues here, `verified`
// drains.
const pendingVerify = new Map();

export function isAvailable() {
  return Boolean(typeof window !== 'undefined' && window.CdvPurchase?.store);
}

function emit(update) {
  for (const cb of listeners) {
    try { cb(update); } catch (e) { console.error('appleProvider listener error', e); }
  }
}

export function onSubscriptionUpdate(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function initializeStore() {
  if (initialised || !isAvailable()) return Promise.resolve();
  initialised = true;

  const { store, ProductType, Platform } = window.CdvPurchase;

  store.register([
    {
      id: APPLE_PRODUCT_IDS[PRODUCTS.PREMIUM_MONTHLY],
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
    {
      id: APPLE_PRODUCT_IDS[PRODUCTS.PREMIUM_YEARLY],
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
  ]);

  // Each handler gets its OWN store.when() call — chaining rejects the whole
  // init if any method in the chain is missing (shared-docs/in-app-purchases.md).
  store.when().approved((transaction) => {
    if (!transaction.transactionId) {
      transaction.finish();
      return;
    }
    const productId = transaction.products?.[0]?.id;
    const sub = APPLE_TO_TIER[productId];
    // Cross-user receipt contamination guard: if this `approved` event
    // fires outside a recent user-initiated purchase/restore window,
    // the receipt belongs to a previous owner of this Apple ID and
    // must NOT be claimed for the current user.
    if (Date.now() - userInitiatedAt >= USER_INITIATED_WINDOW_MS) {
      transaction.finish();
      return;
    }
    // CdvPurchase's Transaction exposes `expirationDate` (a Date), never
    // `expiryDate` — and the Apple adapter rarely populates it, so this skip
    // is best-effort only; the backend apple-verify enforces expiry.
    const expiresAt = transaction.expirationDate ? new Date(transaction.expirationDate) : null;
    const isExpired = Boolean(expiresAt && expiresAt.getTime() <= Date.now());
    // Unknown product = the plugin's virtual `appstore.application`
    // transaction (always first in the Apple receipt, fires approved like a
    // sale) or an unregistered SKU — never queue it for the backend.
    if (!sub || isExpired) {
      transaction.finish();
      return;
    }
    emit({
      tier: sub.tier,
      period: sub.period,
      provider: 'apple',
      productId,
      status: 'active',
      expiresAt: expiresAt ? expiresAt.toISOString() : null,
    });
    pendingVerify.set(transaction.transactionId, productId);
    transaction.verify();
  });

  store.when().verified((receipt) => {
    // `receipt` is a VerifiedReceipt — it has NO .transaction / .productId.
    // The local transactions live on receipt.sourceReceipt.transactions; send
    // exactly what `approved` queued (already window-gated) and only the ids
    // this receipt carries.
    const localIds = new Set(
      (receipt?.sourceReceipt?.transactions || []).map((t) => t.transactionId).filter(Boolean),
    );
    const writes = [];
    for (const [transactionId, productId] of pendingVerify) {
      if (localIds.size && !localIds.has(transactionId)) continue; // another receipt
      pendingVerify.delete(transactionId);
      // silentFetch — see shared-docs/error-surfacing.md.
      writes.push(silentFetch('/subscription/apple-verify', 'POST', { transactionId, productId }));
    }
    receipt.finish();
    if (writes.length) {
      // Refetch AFTER the backend row exists — refreshing before the write
      // lands races it and reads back Free.
      Promise.allSettled(writes).then(() => emit({ refetch: true }));
    }
  });
  // Expiration: not a CdvPurchase v13 chain method. Apple S2S notification
  // (DID_FAIL_TO_RENEW / EXPIRED) hits /subscription/apple-webhook server-side
  // and downgrades the user — the next refreshSubscription() then propagates.

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => { if (!settled) { settled = true; resolve(); } };
    store.ready(finish);
    setTimeout(finish, 6000); // safety: never block UI more than 6s
    store.initialize([Platform.APPLE_APPSTORE]);
  });
}

// Active poll for a registered product — a flat store.ready() timeout resolves
// with no products on slow sandbox networks, which put a "still loading" toast
// in front of the App Store reviewer (shared-docs/in-app-purchases.md checklist).
async function waitForProduct(productId, timeoutMs = 15000) {
  const { store, Platform } = window.CdvPurchase;
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const product = store.get(productId, Platform.APPLE_APPSTORE);
    if (product && product.offers?.length) return product;
    if (Date.now() >= deadline) return product || null;
    await new Promise((r) => setTimeout(r, 500));
  }
}

// Localized pricing for a tier, sourced from StoreKit so the paywall always
// shows what Apple will actually charge (and the right currency for the
// storefront). Returns null until the store has loaded the product; callers
// fall back to hardcoded GBP strings.
export function getLocalizedPricing(tierKey) {
  if (!isAvailable()) return null;
  const productId = APPLE_PRODUCT_IDS[tierKey];
  if (!productId) return null;
  const { store, Platform } = window.CdvPurchase;
  const product = store.get(productId, Platform.APPLE_APPSTORE);
  const phases = product?.offers?.[0]?.pricingPhases;
  if (!phases?.length) return null;

  // Last phase is the recurring price; an earlier zero-price phase is a trial.
  const recurring = phases[phases.length - 1];
  const trialPhase = phases.length > 1 ? phases.find(
    (p) => p !== recurring && (p.priceMicros === 0 || /trial/i.test(p.paymentMode || ''))
  ) : null;

  let perMonth = null;
  if (recurring.priceMicros && recurring.currency && /P1Y/i.test(recurring.billingPeriod || '')) {
    try {
      perMonth = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: recurring.currency,
      }).format(recurring.priceMicros / 1e6 / 12);
    } catch (e) { /* fall back to no per-month figure */ }
  }

  return {
    price: recurring.price || null,
    perMonth,
    hasTrial: Boolean(trialPhase),
  };
}

export async function purchaseProduct(tierKey) {
  if (!isAvailable()) {
    throw new Error('In-app purchases are not available in this build.');
  }
  const productId = APPLE_PRODUCT_IDS[tierKey];
  if (!productId) throw new Error(`Unknown Apple tier: ${tierKey}`);

  const product = await waitForProduct(productId);
  if (!product) {
    throw new Error('Product not available yet. Please try again in a moment.');
  }
  const offer = product.offers?.[0];
  if (!offer) throw new Error('No purchase offer available.');

  const { store } = window.CdvPurchase;
  userInitiatedAt = Date.now();
  // store.order() resolves with an IError on failure — does NOT throw.
  const result = await store.order(offer);
  if (result && result.isError) {
    if (CANCEL_CODES.has(result.code) || /cancel/i.test(result.message || '')) {
      const cancelled = new Error('canceled');
      cancelled.cancelled = true;
      throw cancelled;
    }
    throw new Error(result.message || `Purchase failed (code ${result.code || 'unknown'})`);
  }
}

export async function restorePurchases() {
  if (!isAvailable()) return undefined;
  userInitiatedAt = Date.now();
  // Same contract as store.order(): a StoreKit restore failure RESOLVES with
  // an IError rather than throwing, so surface it for the caller's toast.
  const result = await window.CdvPurchase.store.restorePurchases();
  // Cancels, and REFRESH_RECEIPTS (6777011 — the adapter refreshes the receipt
  // AFTER the restored transactions have already been delivered, so a failure
  // there does not mean the restore failed), must NEVER surface as an error:
  // a red toast on a purchase control is the Guideline 2.1(b) pattern.
  if (result && result.isError
      && ![6777006, 6777003, 6777010, 6777011].includes(result.code)
      && !/cancel/i.test(result.message || '')) {
    const err = new Error(result.message || 'Restore failed. Please try again.');
    err.code = result.code;
    throw err;
  }
  return result;
}
